import { useMemo, useState } from 'react';
import { Button, Modal, Form, InputNumber, Input } from 'antd';
import { TFunction, useTranslation } from 'react-i18next';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';

import { AdminListingDetail } from 'services/api';
import { ModalScrollWrapper, ModalFooterWrapper } from 'components/Modal';
import { getCurrencySign } from 'utils/price.utils';
import { getFormattedNumber, numberFormatter } from 'utils/number.utils';
import FormItem from 'components/form/FormItem';

import styles from './PendingPriceModal.module.less';

interface IFormValues {
  pendingPrice: number | null;
}

type Props = {
  listing: AdminListingDetail;
  onClose: () => void;
  onSuccess: (pendingPrice: number) => void;
};

const priceLimit = 999999999;
const getValidationSchema = (t: TFunction) =>
  yup.object().shape({
    pendingPrice: yup
      .number()
      .nullable()
      .required(t('validation.required'))
      .min(0, t('validation.format'))
      .max(
        priceLimit,
        t('PendingPriceModal.ReachedPriceLimit', {
          limit: getFormattedNumber(priceLimit),
        }),
      ),
  });

export default function PendingPriceModal(props: Props) {
  const { t } = useTranslation();
  const { listing, onClose, onSuccess } = props;
  const { buyers_premium, bids, initial_price, pending_price } = listing;
  const listingCurrentPrice = bids?.[0]?.price.value || initial_price!.value;
  const currencySign = getCurrencySign(initial_price!.currency);
  const isPriceSuggested = !pending_price;
  const formDefaultPrice = isPriceSuggested
    ? Math.ceil(
        listingCurrentPrice + (buyers_premium! / 100) * listingCurrentPrice,
      )
    : pending_price.value;
  const validationSchema = useMemo(() => getValidationSchema(t), [t]);
  const { control, handleSubmit } = useForm<IFormValues>({
    mode: 'onTouched',
    resolver: yupResolver(validationSchema),
    defaultValues: {
      pendingPrice: formDefaultPrice,
    },
  });
  const [isEditMode, setIsEditMode] = useState(false);

  const onSubmit = async (values: IFormValues) => {
    onSuccess(values.pendingPrice!);
  };

  return (
    <Modal
      title={null}
      visible
      centered
      closable={false}
      maskClosable={false}
      onCancel={onClose}
      footer={null}
      destroyOnClose
      width={550}
    >
      <h2 className="modal__title">{t('PendingPriceModal.title')}</h2>
      <Form onFinish={handleSubmit(onSubmit)} layout="vertical">
        <Button
          className="btn-icon modal__close"
          size="small"
          icon={<i className="icon icon-close"></i>}
          onClick={onClose}
        ></Button>

        <ModalScrollWrapper>
          <div className={styles.content}>
            <div className={styles.description}>
              {t('PendingPriceModal.description')}
            </div>

            <div>
              <div className={styles.info}>
                {t('PendingPriceModal.BuyersPremium')}: {buyers_premium}%
              </div>
              <div className={styles.info}>
                {t('PendingPriceModal.FinalBid')}: {currencySign}
                {getFormattedNumber(listingCurrentPrice)}
              </div>
            </div>

            <div>
              <div className={styles.price}>
                <strong>{currencySign}</strong>
                {getFormattedNumber(formDefaultPrice)}
              </div>
              <div className={styles.priceLabel}>
                {t(
                  isPriceSuggested
                    ? 'PendingPriceModal.SuggestedPrice'
                    : 'PendingPriceModal.PendingPrice',
                )}
              </div>

              <div>
                {!isEditMode && (
                  <div className={styles.btnBox}>
                    <Button
                      onClick={() => setIsEditMode(true)}
                      type="link"
                      className="with-icon"
                      size="small"
                      icon={<i className="icon icon-edit"></i>}
                    >
                      {t('PendingPriceModal.ChangePrice')}
                    </Button>
                  </div>
                )}
                {isEditMode && (
                  <div className={styles.formBox}>
                    <FormItem
                      name="pendingPrice"
                      control={control}
                      render={({ field }) => (
                        <Input.Group compact className="input-group">
                          <div className="prefix-label prefix-label--lg">
                            {t('PendingPriceModal.input.PendingPrice')}
                          </div>
                          <InputNumber
                            addonBefore={currencySign}
                            addonAfter={
                              initial_price!.currency?.toUpperCase() || 'USD'
                            }
                            placeholder={t(
                              'PendingPriceModal.input.placeholder',
                            )}
                            size="large"
                            precision={0}
                            controls={false}
                            formatter={numberFormatter}
                            {...field}
                          />
                        </Input.Group>
                      )}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        </ModalScrollWrapper>

        <ModalFooterWrapper>
          <div>
            <Button
              onClick={onClose}
              type="link"
              className="custom-link"
              size="large"
            >
              {t('PendingPriceModal.Cancel')}
            </Button>
          </div>
          <div>
            <Button type="primary" htmlType="submit" size="large">
              {t('PendingPriceModal.Confirm')}
            </Button>
          </div>
        </ModalFooterWrapper>
      </Form>
    </Modal>
  );
}
